import { createTheme } from '@mui/material/styles';
import type { PaletteMode } from '@mui/material';
import type { AppThemeMode } from '@/contexts/ThemeContext';

const isDark = (mode: PaletteMode) => mode === 'dark';

export const createRetroTheme = () => {
  const borderColor = 'rgba(0, 255, 65, 0.2)';
  return createTheme({
    palette: {
      mode: 'dark',
      primary: {
        main: '#00ff41',
        light: '#69ff97',
        dark: '#00c932',
        contrastText: '#000',
      },
      secondary: {
        main: '#ff00ff',
        light: '#ff69ff',
        dark: '#c700c7',
        contrastText: '#000',
      },
      error: { main: '#ff0040' },
      warning: { main: '#ffb800' },
      info: { main: '#00d4ff' },
      success: { main: '#00ff41' },
      background: {
        default: '#0a0a0a',
        paper: '#0d1117',
      },
      text: {
        primary: '#00ff41',
        secondary: 'rgba(0, 255, 65, 0.65)',
      },
      divider: 'rgba(0, 255, 65, 0.15)',
    },
    typography: {
      fontFamily: '"Fira Code", "Cascadia Code", "JetBrains Mono", "SF Mono", Menlo, Monaco, Consolas, monospace',
      h1: { fontSize: '2.5rem', fontWeight: 700, letterSpacing: '-0.02em' },
      h2: { fontSize: '2rem', fontWeight: 700 },
      h3: { fontSize: '1.75rem', fontWeight: 600 },
      h4: { fontSize: '1.5rem', fontWeight: 600 },
      h5: { fontSize: '1.25rem', fontWeight: 600 },
      h6: { fontSize: '1rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em' },
    },
    shape: { borderRadius: 4 },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'uppercase',
            fontWeight: 700,
            letterSpacing: '0.05em',
            border: '1px solid rgba(0, 255, 65, 0.3)',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            boxShadow: '0 0 15px rgba(0, 255, 65, 0.1), 0 0 0 1px rgba(0, 255, 65, 0.15)',
            backgroundImage: 'none',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            borderColor: 'rgba(0, 255, 65, 0.15)',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            borderBottom: '1px solid rgba(0, 255, 65, 0.2) !important',
          },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            boxShadow: '0 0 20px rgba(0, 255, 65, 0.1)',
            border: `1px solid ${borderColor}`,
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            background: 'linear-gradient(135deg, #001a00 0%, #002200 100%)',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderRight: `1px solid ${borderColor}`,
            borderBottom: `1px solid ${borderColor}`,
            '&:last-child': { borderRight: 'none' },
            '.MuiTableHead-root &': {
              borderRight: '1px solid rgba(0, 255, 65, 0.3)',
              borderBottom: '2px solid rgba(0, 255, 65, 0.4)',
              color: '#00ff41',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
            },
          },
        },
      },
    },
  });
};

export const createAppTheme = (mode: PaletteMode | AppThemeMode = 'light') => {
  if (mode === 'retro') return createRetroTheme();

  const dark = isDark(mode as PaletteMode);
  const borderColor = dark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(224, 224, 224, 0.6)';

  return createTheme({
    palette: {
      mode,
      primary: {
        main: dark ? '#5cacee' : '#1976d2',
        light: '#42a5f5',
        dark: '#1565c0',
        contrastText: '#fff',
      },
      secondary: {
        main: dark ? '#ce93d8' : '#9c27b0',
        light: '#ba68c8',
        dark: '#7b1fa2',
        contrastText: '#fff',
      },
      error: {
        main: dark ? '#f44336' : '#d32f2f',
        light: '#ef5350',
        dark: '#c62828',
      },
      warning: {
        main: dark ? '#ffa726' : '#ed6c02',
        light: '#ff9800',
        dark: '#e65100',
      },
      info: {
        main: dark ? '#29b6f6' : '#0288d1',
        light: '#03a9f4',
        dark: '#01579b',
      },
      success: {
        main: dark ? '#66bb6a' : '#2e7d32',
        light: '#4caf50',
        dark: '#1b5e20',
      },
      background: dark ? {
        default: '#0a0a0a',
        paper: '#141414',
      } : {
        default: '#fafafa',
        paper: '#fff',
      },
      text: dark ? {
        primary: '#e8e8e8',
        secondary: 'rgba(255, 255, 255, 0.65)',
      } : {
        primary: 'rgba(0, 0, 0, 0.87)',
        secondary: 'rgba(0, 0, 0, 0.6)',
      },
      divider: dark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
    },
    typography: {
      fontFamily: [
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif',
        '"Apple Color Emoji"',
        '"Segoe UI Emoji"',
        '"Segoe UI Symbol"',
      ].join(','),
      h1: { fontSize: '2.5rem', fontWeight: 600 },
      h2: { fontSize: '2rem', fontWeight: 600 },
      h3: { fontSize: '1.75rem', fontWeight: 600 },
      h4: { fontSize: '1.5rem', fontWeight: 600 },
      h5: { fontSize: '1.25rem', fontWeight: 600 },
      h6: { fontSize: '1rem', fontWeight: 600 },
    },
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiPopover: {
        defaultProps: {
          disableScrollLock: true,
        },
      },
      MuiMenu: {
        defaultProps: {
          disableScrollLock: true,
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            boxShadow: dark
              ? '0 2px 8px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.06)'
              : '0 2px 8px rgba(0,0,0,0.1)',
            backgroundImage: 'none',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            boxShadow: dark ? '0 4px 12px rgba(0, 0, 0, 0.4)' : '0 4px 12px rgba(0, 0, 0, 0.1)',
            borderRadius: 8,
            border: `1px solid ${borderColor}`,
            overflow: 'auto',
            WebkitOverflowScrolling: 'touch',
          },
        },
      },
      MuiTable: {
        styleOverrides: {
          root: {
            width: '100%',
            borderCollapse: 'separate',
            borderSpacing: 0,
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '.MuiTableBody-root &:nth-of-type(even)': {
              backgroundColor: dark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
            },
            '.MuiTableBody-root &:hover': {
              backgroundColor: dark
                ? 'rgba(255, 255, 255, 0.06)'
                : 'rgba(0, 0, 0, 0.04)',
              transition: 'background-color 0.2s ease',
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            padding: '14px 20px',
            fontSize: '0.95rem',
            borderBottom: `1px solid ${borderColor}`,
            transition: 'background-color 0.2s ease',
            '.MuiTableHead-root &': {
              fontWeight: 700,
              letterSpacing: '0.02em',
              textTransform: 'uppercase',
              fontSize: '0.85rem',
              color: dark ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.7)',
              borderBottom: `2px solid ${borderColor}`,
            },
            '.MuiTableBody-root .MuiTableRow-root:last-child &': { borderBottom: 'none' },
          },
        },
      },
    },
  });
};

// Default light theme for SSR
export const theme = createAppTheme('light');

export default theme;
